/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useMemo, useState } from 'react';

import { styled } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { NLPSearchErrorState } from '../../../../ai-answer-dialog/nlp-search-error-state';
import { AiBrand } from '../../../../constants';
import {
	ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK,
	ATLASSIAN_AI_TROUBLESHOOT_LINK,
	ATLASSIAN_HIPAA_COMPLICANCE_LINK,
} from '../../common/constants';
import { DefinitionUpdateConfirmationFlag } from '../../curated/edit-definition-modal/definition-update-confirmation-flag';
import { EditDefinitionModal } from '../../curated/edit-definition-modal/edit-definition-modal';

import { messages } from './messages';

const styles = cssMap({
	errorPadding: {
		paddingBottom: token('space.100'),
	},
	errorMessage: {
		font: token('font.body'),
		color: token('color.text'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
const InfoLink = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	color: `${token('color.text.accent.blue')} !important`,
	textDecoration: 'underline',
});

export const DefinitionError: () => JSX.Element = () => {
	const { formatMessage } = useIntl();
	const {
		brand,
		commonAttributes: { errorState },
		readingAidsOptions,
	} = useAIAnswerContext();

	const { definitionCurationOptions } = readingAidsOptions || {};
	const createDefinitionEnabled = !!definitionCurationOptions;

	const [showEditDefinitionModal, setShowEditDefinitionModal] = useState(false);
	const [displaySuccessFlag, setDisplaySuccessFlag] = useState(false);

	const showAddDefinitionButton =
		createDefinitionEnabled &&
		(errorState === NLPSearchErrorState.NoAnswer ||
			errorState === NLPSearchErrorState.NoAnswerKeywords ||
			errorState === NLPSearchErrorState.NoAnswerSearchResults ||
			errorState === NLPSearchErrorState.NoAnswerHydration ||
			errorState === NLPSearchErrorState.NoAnswerRelevantContent ||
			errorState === NLPSearchErrorState.Default);

	const errorMessage = useMemo(() => {
		switch (errorState) {
			case NLPSearchErrorState.AcceptableUseViolation:
				return (
					<FormattedMessage
						{...messages.definition_no_answer_acceptable_use_error}
						values={{
							acceptableUseLink: (
								<InfoLink href={ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK} target={'_blank'}>
									{formatMessage(messages.definition_no_answer_acceptable_use_link)}
								</InfoLink>
							),
							brand: brand === AiBrand.AI ? 'Atlassian Intelligence' : 'Rovo',
						}}
					/>
				);
			case NLPSearchErrorState.NetworkError:
			case NLPSearchErrorState.NoAnswerOpenAIResponseError:
			case NLPSearchErrorState.AIDisabled:
				return (
					<FormattedMessage
						{...messages.definition_no_answer_network_error}
						values={{
							brand: brand === AiBrand.AI ? 'Atlassian Intelligence' : 'Rovo',
							supportLink: (
								<InfoLink href={ATLASSIAN_AI_TROUBLESHOOT_LINK} target={'_blank'}>
									{formatMessage(messages.definitions_support_link_text)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.HIPAAError:
				return (
					<FormattedMessage
						{...messages.definitions_no_answer_error_state_hipaa}
						values={{
							supportLink: (
								<InfoLink href={ATLASSIAN_HIPAA_COMPLICANCE_LINK} target={'_blank'}>
									{formatMessage(messages.definitions_no_answer_hipaa_compliance_link)}
								</InfoLink>
							),
						}}
					/>
				);
			case NLPSearchErrorState.NoAnswer:
			case NLPSearchErrorState.NoAnswerKeywords:
			case NLPSearchErrorState.NoAnswerSearchResults:
			case NLPSearchErrorState.NoAnswerHydration:
			case NLPSearchErrorState.NoAnswerRelevantContent:
			case NLPSearchErrorState.Default:
			default:
				return (
					<FormattedMessage
						{...messages.definition_no_answer_error}
						values={{
							brand: brand === AiBrand.AI ? 'Atlassian Intelligence' : 'Rovo',
						}}
					/>
				);
		}
	}, [errorState, formatMessage, brand]);

	return (
		<Fragment>
			<Box xcss={styles.errorPadding}>
				<Box xcss={styles.errorMessage}>{errorMessage}</Box>
				{showAddDefinitionButton && (
					<Flex justifyContent="end" alignItems="center">
						<Button appearance="default" onClick={() => setShowEditDefinitionModal(true)}>
							{formatMessage(messages.definition_no_answer_add_definition_button)}
						</Button>
					</Flex>
				)}
			</Box>
			<ModalTransition>
				{showEditDefinitionModal && showAddDefinitionButton && (
					<EditDefinitionModal
						curatedDefinitionScopeConfig={definitionCurationOptions.curatedDefinitionScopeConfig}
						closeEditModal={() => setShowEditDefinitionModal(false)}
						isCreateDefinition={true}
						onSaveEditModal={() => setDisplaySuccessFlag(true)}
					/>
				)}
			</ModalTransition>
			{displaySuccessFlag && (
				<DefinitionUpdateConfirmationFlag onDismissed={() => setDisplaySuccessFlag(false)} />
			)}
		</Fragment>
	);
};
